import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Navigation state lives in the query string, not in component state.
 *
 * The reason is the refresh: a person sitting on RT Logs who reloads the page
 * has to come back to RT Logs, not to Overview. The same applies to the back
 * button, to a bookmark, and to a link pasted into a chat — all of them work
 * only if where you are is written down in the URL.
 */
export function useQueryNav() {
  const [params, setParams] = useSearchParams();

  /**
   * Apply a patch to the query string. A null or empty value removes the key,
   * so a value that is back at its default leaves no trace in the URL.
   *
   * Pass replace for changes nobody wants to walk back through one at a time —
   * paging, for instance — and leave it off for real navigation.
   */
  const set = useCallback(
    (patch: Record<string, string | null>, options?: { replace?: boolean }) => {
      const next = new URLSearchParams(params);

      for (const [key, value] of Object.entries(patch)) {
        if (value === null || value === '') next.delete(key);
        else next.set(key, value);
      }

      setParams(next, { replace: options?.replace ?? false });
    },
    [params, setParams],
  );

  return { params, set };
}
